/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { getData } from "utils/Gateway";
import { reactSelectStyle, getCustomStyles } from "Config/Config";
import FileUploadField from "components/Common/FileUploadField/FileUploadField";
import JoditEditorField from "components/Common/JoditEditorField/JoditEditorField";
import { getMemberOptions } from "helper/ContactHelper/ContactHelper";

// Helper for rendering input fields
const InputField = ({
  id,
  label,
  value,
  onChange,
  error,
  type = "text",
  placeholder = "",
  required = false,
  ...props
}) => (
  <div className={props.className || ""}>
    <label htmlFor={id} className="d-block fs-sm fw-semibold mb-2">
      {label} {required && "*"}
    </label>
    <input
      type={type}
      id={id}
      className={`form-control fs-sm shadow-none ${
        error ? "is-invalid" : "border border-gray-300"
      }`}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      {...props}
    />
    {error && <div className="invalid-feedback">{error}</div>}
  </div>
);

// Helper for rendering Select fields
const SelectField = ({
  id,
  label,
  value,
  onChange,
  error,
  options,
  isMulti = false,
  isClearable = false,
  placeholder = "",
  required = false,
  styles,
  ...props
}) => (
  <div className={props.className || ""}>
    <label htmlFor={id} className="d-block fs-sm fw-semibold mb-2">
      {label} {required && "*"}
    </label>
    <Select
      id={id}
      isMulti={isMulti}
      isClearable={isClearable}
      styles={styles || getCustomStyles(error)}
      placeholder={placeholder}
      options={options}
      value={value}
      onChange={onChange}
      {...props}
    />
    {error && <div className="invalid-feedback d-block">{error}</div>}
  </div>
);

const CompanyForm = ({
  isSaving,
  initialData = {},
  onSubmit,
  companyTypeList,
  serviceList,
  searchingList,
  interestList,
  onUploadLogo,
  logoImage,
  isImageUploading,
  onRemoveLogo,
  onUploadDocs,
  uploadedFiles,
  onRemoveDoc,
  isFileUploading,
  params,
}) => {
  const { t } = useTranslation();
  const formRef = useRef(null);

  const [followersInput, setFollowersInput] = useState("");
  const [followersOptions, setFollowersOptions] = useState([]);

  // Form field states
  const [cif, setCif] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [establishedYear, setEstablishedYear] = useState("");
  const [billings, setBillings] = useState(0);
  const [noOfEmployees, setNoOfEmployees] = useState(0);
  const [authKey, setAuthKey] = useState("");
  const [companySummary, setCompanySummary] = useState("");
  const [companyDetails, setCompanyDetails] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [website, setWebsite] = useState("");
  const [companyTypeValue, setCompanyTypeValue] = useState(null);
  const [serviceValue, setServiceValue] = useState(null);
  const [searchingValue, setSearchingValue] = useState(null);
  const [interestValue, setInterestValue] = useState(null);
  const [stateName, setstateName] = useState("");
  const [cityName, setcityName] = useState("");
  const [country, setcountry] = useState("");
  const [zipcode, setzipcode] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [followrsValue, setFollowrsValue] = useState(null);
  const [selectedFollowers, setSelectedFollowers] = useState([]);

  // New states for accreditation and clinic characteristics
  const [localAccreditationCAM, setLocalAccreditationCAM] = useState("");
  const [localClinicCharacteristics, setLocalClinicCharacteristics] = useState({
    squaremeters: 0,
    patientspermonth: 0,
    schedule: "",
    authorizations: {
      healthcouncil: {
        number: "",
        expiration: "",
      },
    },
  });

  const [errors, setErrors] = useState({});

  //function for get followers list
  const getFollowersList = async () => {
    try {
      const data = await getMemberOptions(followersInput);

      setFollowersOptions(data);
    } catch (error) {
      setMessageType("error");
      setAlertMessage(error.message);
      setShowAlert(true);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!cif.trim()) newErrors.cif = t("Please enter the company CIF.");
    if (!name.trim()) newErrors.name = t("Please enter the company name.");
    if (!companyTypeValue) newErrors.type = t("Please select a company type.");
    if (!email.trim() || (email.trim() && !/\S+@\S+\.\S+/.test(email))) {
      newErrors.email = t("Please enter a valid email address.");
    }
    if (!phone.trim()) newErrors.phone = t("Please enter the phone number.");
    if (!address.trim()) newErrors.address = t("Please enter the address.");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = () => {
    if (!validateForm()) {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: "smooth" });
      }
      return;
    }

    const companyData = {
      name,
      cif,
      email,
      website,
      companytype: companyTypeValue?.value || null,
      summary: companySummary,
      about: companyDetails,
      location: address,
      phone,
      city: cityName,
      state: stateName,
      country,
      zipcode: zipcode,
      latitude: latitude,
      longitude: longitude,
      services: serviceValue ? serviceValue.map((item) => item.value) : [],
      searching: searchingValue ? searchingValue.map((item) => item.value) : [],
      interest: interestValue ? interestValue.map((item) => item.value) : [],
      establishedyear: establishedYear,
      billing: billings,
      noofemployees: noOfEmployees,
      authkey: authKey,
      accreditationCAM: localAccreditationCAM,
      cliniccharacteristics: localClinicCharacteristics,
      followers: selectedFollowers,
    };
    onSubmit(companyData);
  };

  const getLocationFromZip = async (e) => {
    e.preventDefault();
    if (zipcode !== "") {
      try {
        const url = `https://maps.googleapis.com/maps/api/geocode/json?components=postal_code:${zipcode}&region=es&key=AIzaSyDt4dbtdHCHLj8grTzTVxpztvqUGDkvkaI`;
        const response = await getData(url);

        if (response.status === "OK") {
          const results = response.results;
          if (results.length > 0) {
            results[0]?.address_components.forEach((item) => {
              if (item.types[0] === "country") setcountry(item?.long_name);
              if (item.types[0] === "locality") setcityName(item?.long_name);
              if (item.types[0] === "administrative_area_level_1")
                setstateName(item?.long_name);
            });
            setLatitude(results[0]?.geometry?.location?.lat);
            setLongitude(results[0]?.geometry?.location?.lng);
          }
        }
      } catch (error) {
        console.log("Error:", error.message);
      }
    }
  };

  // Effect to populate form when initialData is provided (for editing)
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setCif(initialData.cif ?? "");
      setName(initialData.name ?? "");
      setEmail(initialData.email ?? "");
      setEstablishedYear(initialData.establishedyear ?? "");
      setBillings(initialData.billing ?? 0);
      setNoOfEmployees(initialData.noofemployees ?? 0);
      setCompanySummary(initialData.summary ?? "");
      setCompanyDetails(initialData.about ?? "");
      setPhone(initialData.phone ?? "");
      setAddress(initialData.location ?? "");
      setWebsite(initialData.website ?? "");
      setAuthKey(initialData?.authkey ?? "");
      if (initialData.companytype) setCompanyTypeValue(initialData.companytype);
      if (initialData.companyservices)
        setServiceValue(initialData.companyservices);
      if (initialData.companysearching)
        setSearchingValue(initialData.companysearching);
      if (initialData.companyinterest)
        setInterestValue(initialData.companyinterest);
      if (initialData.companyfollowers) {
        setFollowrsValue(initialData.companyfollowers);
        setSelectedFollowers(
          initialData.companyfollowers.map((item) => item.value)
        );
      }
      setstateName(initialData.state ?? "");
      setcityName(initialData.city ?? "");
      setcountry(initialData.country ?? "");
      setzipcode(initialData.zipcode ?? "");
      setLatitude(initialData.latitude ?? "");
      setLongitude(initialData.longitude ?? "");

      // Populate new states from initialData
      setLocalAccreditationCAM(initialData.accreditationCAM ?? "");
      setLocalClinicCharacteristics(
        initialData.cliniccharacteristics ?? {
          squaremeters: 0,
          patientspermonth: 0,
          schedule: "",
          authorizations: {
            healthcouncil: {
              number: "",
              expiration: "",
            },
          },
        }
      );
    }
  }, [initialData]);

  useEffect(() => {
    if (followersInput.length > 0) {
      getFollowersList();
    }
  }, [followersInput]);

  return (
    <div
      ref={formRef}
      className="p-3 p-lg-4 border border-gray-300 rounded-10 shadow-sm"
    >
      <form onSubmit={(e) => e.preventDefault()}>
        {/* Cif / Type */}
        <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
          <div className="row">
            <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
              <InputField
                id="cif"
                label={t("Cif")}
                value={cif}
                onChange={(e) => {
                  setCif(e.target.value);
                  if (errors.cif) setErrors((prev) => ({ ...prev, cif: "" }));
                }}
                error={errors.cif}
                placeholder={t("Enter company CIF")}
                required
              />
            </div>
            <div className="col-lg-6">
              <SelectField
                id="type"
                label={t("Type")}
                value={companyTypeValue}
                onChange={(val) => {
                  setCompanyTypeValue(val);
                  if (errors.type) setErrors((prev) => ({ ...prev, type: "" }));
                }}
                error={errors.type}
                options={companyTypeList}
                isClearable
                placeholder={t("Select Company Type")}
                required
              />
            </div>
          </div>
        </div>

        {/* Name / Email */}
        <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
          <div className="row">
            <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
              <InputField
                id="name"
                label={t("Name")}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) setErrors((prev) => ({ ...prev, name: "" }));
                }}
                error={errors.name}
                placeholder={t("Enter company name")}
                required
              />
            </div>
            <div className="col-lg-6">
              <InputField
                id="email"
                label={t("Email")}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email)
                    setErrors((prev) => ({ ...prev, email: "" }));
                }}
                error={errors.email}
                placeholder={t("Enter company email address")}
                type="email"
              />
            </div>
          </div>
        </div>

        {/* Location / Phone */}
        <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
          <div className="row">
            <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
              <InputField
                id="address"
                label={t("Location")}
                value={address}
                onChange={(e) => {
                  setAddress(e.target.value);
                  if (errors.address)
                    setErrors((prev) => ({ ...prev, address: "" }));
                }}
                error={errors.address}
                placeholder={t("Enter full address")}
              />
            </div>
            <div className="col-lg-6">
              <InputField
                id="phone"
                label={t("Phone")}
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  if (errors.phone)
                    setErrors((prev) => ({ ...prev, phone: "" }));
                }}
                error={errors.phone}
                placeholder={t("Enter phone number")}
                required
              />
            </div>
          </div>
        </div>

        {/* Website / Establishment Year */}
        <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
          <div className="row">
            <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
              <InputField
                id="website"
                label={t("Website")}
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="www.example.com"
              />
            </div>
            <div className="col-lg-6">
              <InputField
                id="establishmentYear"
                label={t("Establishment Year")}
                value={establishedYear}
                onChange={(e) => setEstablishedYear(e.target.value)}
                type="number"
                placeholder="e.g., 2010"
              />
            </div>
          </div>
        </div>

        {/* Zipcode / Location fetch */}
        <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
          <div className="row">
            <div className="col-lg-4 mb-3 mb-lg-0">
              <div className="row g-2">
                <div className="col">
                  <InputField
                    id="zipcode"
                    value={zipcode}
                    onChange={(e) => setzipcode(e.target.value)}
                    placeholder={t("Enter zip code")}
                  />
                </div>
                <div className="col-auto">
                  <button
                    className="btn btn-primary"
                    onClick={getLocationFromZip}
                  >
                    {t("Fetch Location")}
                  </button>
                </div>
              </div>
            </div>
            <div className="col-lg-8">
              <div className="row">
                <div className="col-lg-4 mb-3 mb-lg-0">
                  <InputField
                    id="city"
                    value={cityName}
                    onChange={(e) => setcityName(e.target.value)}
                    placeholder={t("City")}
                  />
                </div>
                <div className="col-lg-4 mb-3 mb-lg-0">
                  <InputField
                    id="state"
                    value={stateName}
                    onChange={(e) => setstateName(e.target.value)}
                    placeholder={t("State/Province")}
                  />
                </div>
                <div className="col-lg-4">
                  <InputField
                    id="country"
                    value={country}
                    onChange={(e) => setcountry(e.target.value)}
                    placeholder={t("Country")}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Latitude / Longitude */}
        <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
          <div className="row">
            <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
              <InputField
                id="latitude"
                label={t("Latitude")}
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                placeholder="e.g., 40.7128"
              />
            </div>
            <div className="col-lg-6">
              <InputField
                id="longitude"
                label={t("Longitude")}
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                placeholder="e.g., -74.0060"
              />
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
          <label htmlFor="resume" className="d-block fs-sm fw-semibold mb-2">
            {t("Summary")}
          </label>
          <textarea
            name="resume"
            id="resume"
            cols="30"
            rows="5"
            className="form-control fs-sm shadow-none border border-gray-300"
            placeholder={t("Provide a brief summary of the company.")}
            value={companySummary}
            onChange={(e) => setCompanySummary(e.target.value)}
          ></textarea>
        </div>

        {/* Details */}
        <div className="form-group mb-4 p-3 pb-0 border rounded-8 shadow-sm bg-white">
          <JoditEditorField
            label={t("Details")}
            value={companyDetails}
            labelClassName="d-block fs-sm fw-semibold mb-2"
            onChange={(newContent) => setCompanyDetails(newContent)}
          />
        </div>

        {/* Billing / Employees / AuthKey */}
        <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
          <div className="row">
            <div className="col-lg-4 mb-3 mb-sm-4 mb-lg-0">
              <InputField
                id="billing"
                label={t("Billing")}
                value={billings}
                onChange={(e) => setBillings(e.target.value)}
                type="number"
                placeholder="e.g., 1000000"
              />
            </div>
            <div className="col-lg-4">
              <InputField
                id="noOfEmployees"
                label={t("Number of Employees")}
                value={noOfEmployees}
                onChange={(e) => setNoOfEmployees(e.target.value)}
                type="number"
                placeholder="e.g., 50"
              />
            </div>
            <div className="col-lg-4">
              <InputField
                id="authkey"
                label={t("Authkey")}
                value={authKey}
                onChange={(e) => setAuthKey(e.target.value)}
                placeholder={t("Enter the authentication key")}
              />
            </div>
          </div>
        </div>

        {/* Accreditation CAM */}
        <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
          <InputField
            id="accreditationCAM"
            label={t("Accreditation CAM")}
            value={localAccreditationCAM}
            onChange={(e) => setLocalAccreditationCAM(e.target.value)}
            placeholder={t("Enter Accreditation CAM")}
          />
        </div>

        {/* Clinic Characteristics */}
        <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
          <label className="d-block fs-md fw-semibold mb-2">
            {t("Clinic Characteristics")}
          </label>
          <div className="row">
            <div className="col-lg-4 mb-3 mb-sm-4 mb-lg-0">
              <InputField
                id="squaremeters"
                label={t("Square Meters")}
                value={localClinicCharacteristics.squaremeters}
                onChange={(e) =>
                  setLocalClinicCharacteristics((prev) => ({
                    ...prev,
                    squaremeters: Number(e.target.value),
                  }))
                }
                type="number"
                placeholder="e.g., 100"
              />
            </div>
            <div className="col-lg-4 mb-3 mb-sm-4 mb-lg-0">
              <InputField
                id="patientspermonth"
                label={t("Patients Per Month")}
                value={localClinicCharacteristics.patientspermonth}
                onChange={(e) =>
                  setLocalClinicCharacteristics((prev) => ({
                    ...prev,
                    patientspermonth: Number(e.target.value),
                  }))
                }
                type="number"
                placeholder="e.g., 500"
              />
            </div>
            <div className="col-lg-4">
              <InputField
                id="schedule"
                label={t("Schedule")}
                value={localClinicCharacteristics.schedule}
                onChange={(e) =>
                  setLocalClinicCharacteristics((prev) => ({
                    ...prev,
                    schedule: e.target.value,
                  }))
                }
                type="time"
                placeholder="e.g., Mon-Fri, 9 AM - 5 PM"
              />
            </div>
          </div>
          <div className="row mt-3">
            <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
              <InputField
                id="healthcouncilNumber"
                label={t("Health Council Authorization Number")}
                value={
                  localClinicCharacteristics.authorizations.healthcouncil.number
                }
                onChange={(e) =>
                  setLocalClinicCharacteristics((prev) => ({
                    ...prev,
                    authorizations: {
                      ...prev.authorizations,
                      healthcouncil: {
                        ...prev.authorizations.healthcouncil,
                        number: e.target.value,
                      },
                    },
                  }))
                }
                placeholder="Enter number"
              />
            </div>
            <div className="col-lg-6">
              <InputField
                id="healthcouncilExpiration"
                label={t("Health Council Authorization Expiration")}
                value={
                  localClinicCharacteristics.authorizations.healthcouncil
                    .expiration
                }
                onChange={(e) =>
                  setLocalClinicCharacteristics((prev) => ({
                    ...prev,
                    authorizations: {
                      ...prev.authorizations,
                      healthcouncil: {
                        ...prev.authorizations.healthcouncil,
                        expiration: e.target.value,
                      },
                    },
                  }))
                }
                type="date"
              />
            </div>
          </div>
        </div>

        {/* Services / Searching / Interest */}
        <SelectField
          id="services"
          label={t("Services")}
          value={serviceValue}
          onChange={(val) => setServiceValue(val)}
          options={serviceList}
          isMulti
          placeholder={t("Select services offered")}
        />
        <SelectField
          id="searching"
          label={t("Searching")}
          value={searchingValue}
          onChange={(val) => setSearchingValue(val)}
          options={searchingList}
          isMulti
          placeholder={t("Select what the company is searching for")}
        />
        <SelectField
          id="interest"
          label={t("Interest")}
          value={interestValue}
          onChange={(val) => setInterestValue(val)}
          options={interestList}
          isMulti
          placeholder={t("Select company interests")}
        />

        {/* Followers */}
        <SelectField
          id="followers"
          label={t("Followers")}
          value={followrsValue}
          onChange={(val) => {
            setFollowrsValue(val);
            setSelectedFollowers(val.map((item) => item.value));
          }}
          options={followersOptions}
          isMulti
          placeholder={t("Type user name or email")}
          onInputChange={(val) => setFollowersInput(val)}
        />

        {/* Company Logo */}
        <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
          <label htmlFor="logo" className="d-block fs-sm fw-semibold mb-2">
            {t("Company Logo")}
          </label>
          <FileUploadField
            label={t("Upload Company Logo")}
            accept="jpg png jpeg"
            onUpload={onUploadLogo}
            files={
              logoImage
                ? [{ path: logoImage, name: "Company Logo" }]
                : [
                    {
                      path: "https://placehold.co/150x150",
                      name: "Company Logo",
                    },
                  ]
            }
            onRemove={onRemoveLogo}
            loading={isImageUploading}
            fileType="image"
          />
        </div>

        {/* Attachments */}
        <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
          <label
            htmlFor="attachments"
            className="d-block fs-sm fw-semibold mb-2"
          >
            {t("Attachments")}
          </label>
          <FileUploadField
            label={t("Attachments")}
            accept="doc mp4 pdf"
            onUpload={onUploadDocs}
            files={uploadedFiles}
            onRemove={onRemoveDoc}
            loading={isFileUploading}
            multiple
            fileType="document"
          />
        </div>

        {/* Actions */}
        <div className="action d-flex flex-wrap gap-3 align-items-center justify-content-between">
          <Link
            to="/admin/companies"
            className="btn btn-outline-primary d-flex align-items-center gap-2"
          >
            <span className="material-symbols-outlined">cancel</span>
            {t("Close")}
          </Link>
          <button
            type="button"
            className="btn btn-primary d-flex align-items-center gap-2"
            style={{ cursor: isSaving ? "not-allowed" : "pointer" }}
            disabled={isSaving}
            onClick={handleFormSubmit}
          >
            {isSaving ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                {t("Saving...")}
              </>
            ) : (
              <>
                <span className="material-symbols-outlined">save</span>
                {params.id ? t("Update Company") : t("Save Company")}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanyForm;
